import { useColorScheme } from "react-native";

import { Colors, type ThemeColors } from "@/constants/theme";
import { useThemePreference } from "@/theme/theme-preference-store";

export type Scheme = "light" | "dark";

/**
 * The resolved light/dark scheme, honouring the user's saved choice.
 *
 * This serves the 58 files that are not tabs — every screen pushed on top of
 * one: Invite, Language, Upgrade, Support, Dashboard. `useAppTheme` and
 * `useResolvedAppColors` serve the tabs and the root view.
 *
 * **It used to read `themePreference` off the auth store through a cast**, and
 * that field stopped existing when the preference was moved to its own store —
 * so this silently resolved `device` forever. Nothing failed: a cast to a type
 * with an optional field cannot fail, and `?? "device"` turned "the field is
 * gone" into a plausible answer. The visible result was a light-theme user
 * whose tabs were light and whose every pushed screen was dark, because the two
 * resolvers had quietly stopped agreeing.
 *
 * The lesson is in the shape rather than the value: a cast is how one reader of
 * a moved field gets left behind, because it is the one access that the
 * compiler will not check. Both resolvers now import the same store, and
 * `tests/mobile/theme-preference.test.ts` scans for any file that reaches for a
 * theme preference anywhere else.
 */
export function useScheme(): Scheme {
  const system = useColorScheme();
  const preference = useThemePreference();

  if (preference === "light") return "light";
  if (preference === "dark") return "dark";
  return system === "dark" ? "dark" : "light";
}

export function useTheme(): ThemeColors {
  return Colors[useScheme()];
}
